/**
 * Why an editor instance tab left the strip.
 *
 * `discard` is the user closing it (or a scope reset). `partition` is the
 * workbench swapping to another key: the tab is still that key's document, so
 * a runtime must not treat it as thrown away (见 03 / 22).
 */
export type EditorInstanceCloseReason = 'discard' | 'partition';

/** `applyingPartition` is true while a partition snapshot is being applied. */
export function editorInstanceCloseReason(
  applyingPartition: boolean,
): EditorInstanceCloseReason {
  return applyingPartition ? 'partition' : 'discard';
}

/**
 * Editor instance-tab close detection (架构方案 §5.7).
 *
 * `DockviewTabHost.getTabs()` only lists panels that still share the area
 * anchor's group. Dragging a tab into its own group removes it from that list
 * without removing the panel. Treating that as a close disposes module state
 * (the built-in browser slot) while the tab is still on screen.
 */

/**
 * Reconcile the set of editor instances whose close must still be observed.
 *
 * `inAnchorGroup` is the current anchor-group snapshot. An id that left that
 * group but `isStillOpen` stays tracked; an id that is gone from the layout is
 * reported in `closed`.
 */
export function diffClosedEditorInstances(
  tracked: ReadonlySet<string>,
  inAnchorGroup: ReadonlySet<string>,
  isStillOpen: (instanceId: string) => boolean,
): { tracked: Set<string>; closed: string[] } {
  const next = new Set(inAnchorGroup);
  const closed: string[] = [];
  for (const id of tracked) {
    if (next.has(id)) {
      continue;
    }
    if (isStillOpen(id)) {
      next.add(id);
      continue;
    }
    closed.push(id);
  }
  return { tracked: next, closed };
}
